import React, { useState } from 'react';
import moment from 'moment';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { formatMoney } from '../../../util/currency';
import { IconSpinner } from '../../../components';
import AddToUserModal from '../AddToUserModal';

import css from './Summary.css';

const format = (intl, date) =>
  intl.formatDate(date, {
    weekday: 'short',
    month: 'short',
    year: 'numeric',
    day: 'numeric',
  });

const Summary = ({
  start,
  end,
  payinTotal,
  lineItems,
  cartListings,
  intl,
  handleSubmit,
  provider,
  id,
  totalNumberOfOrders,
  onAddCartToUser,
  isAddToUserAvailable,
  canBookInternally,
  isBookDisabled = false,
  inProgress = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const PRODUCT_CODE = 'day';
  const LONG_TERM_DISCOUNT = 'long-term-discount';
  const EVENING_DISCOUNT = 'evening-discount';
  const PARTNERSHIP_DISCOUNT = 'partnership-discount';

  const handleBook = () => {
    handleSubmit({
      start,
      end,
      cartListings,
      provider,
      cart: id,
      totalNumberOfOrders,
    });
  };

  // Nowa funkcja dla rezerwacji wewnętrznej
  const handleBookInternally = () => {
    handleSubmit({
      start,
      end,
      cartListings,
      provider,
      cart: id,
      totalNumberOfOrders,
      internalUse: { reason: 's' },
    });
  };

  const handleOpenUserModal = () => {
    setIsModalOpen(true);
  };

  const listingOrderMap = new Map(
    cartListings.map((cl, index) => [cl.listing?.id?.uuid, index])
  );

  const products = lineItems
    .filter(li => !!li)
    .sort((liA, liB) => {
      const indexA = listingOrderMap.get(liA.listingId) ?? Infinity;
      const indexB = listingOrderMap.get(liB.listingId) ?? Infinity;
      return indexA - indexB;
    })
    .map(({ code, listingId, quantity, lineTotal }) => {
      if (code === PRODUCT_CODE) {
        const listingItem = cartListings.find(
          ({ listing }) => listing?.id?.uuid === listingId
        );

        // Zabezpieczenie przed błędem, gdyby listingItem było undefined
        const title = listingItem?.listing?.attributes?.title || 'Produkt';

        return (
          <div className={css.rowContainer} key={listingId}>
            <p className={css.productText}>
              {`${title} x `}
              <FormattedMessage id="Summary.day" values={{ count: quantity }} />
            </p>
            <p className={css.productText}>{formatMoney(intl, lineTotal)}</p>
          </div>
        );
      }

      if (code === LONG_TERM_DISCOUNT) {
        return (
          <div className={classNames(css.rowContainer, css.discount)} key={LONG_TERM_DISCOUNT}>
            <p className={css.productText}>
              <FormattedMessage id="Summary.discountText" />
            </p>
            <p className={css.productText}>{formatMoney(intl, lineTotal)}</p>
          </div>
        );
      }

      if (code === EVENING_DISCOUNT) {
        return (
          <div className={classNames(css.rowContainer, css.discount)} key={EVENING_DISCOUNT}>
            <p className={css.productText}>
              <FormattedMessage id="Summary.eveningDiscountText" />
            </p>
            <p className={css.productText}>{formatMoney(intl, lineTotal)}</p>
          </div>
        );
      }

      if (code === PARTNERSHIP_DISCOUNT) {
        return (
          <div className={classNames(css.rowContainer, css.discount)} key={PARTNERSHIP_DISCOUNT}>
            <p className={css.productText}>
              <FormattedMessage id="Summary.partnershipDiscountText" />
            </p>
            <p className={css.productText}>{formatMoney(intl, lineTotal)}</p>
          </div>
        );
      }

      return null; // Dobra praktyka: zwrócenie null, jeśli żaden if nie zostanie spełniony
    });

  const activeProgramPoints =
    process.env.REACT_APP_COUNTRY_ID === 'pl' ? (
      <div className={css.rowContainer}>
        <p className={css.productText}>
          <FormattedMessage id="Summary.textPoints" />
        </p>
        <p className={css.productText}>
          <FormattedMessage id="Summary.points" />
        </p>
      </div>
    ) : null;

  return (
    <div className={css.summaryCard}>
      <p className={css.title}>
        <FormattedMessage id="Summary.summaryTitle" />
      </p>
      <div className={css.productSection}>
        <div className={css.rowContainer}>
          <p className={css.productText}>
            <FormattedMessage id="Summary.bookingStartTitle" />
          </p>
          <p className={css.productText}>{format(intl, moment(start))}</p>
        </div>
        <div className={css.rowContainer}>
          <p className={css.productText}>
            <FormattedMessage id="Summary.bookingEndTitle" />
          </p>
          <p className={css.productText}>{format(intl, moment(end).subtract(1, 'days'))}</p>
        </div>
      </div>
      <div className={css.productSection}>{products}</div>
      <div className={css.togetherSection}>
        {activeProgramPoints}
        <div className={css.rowContainer}>
          <p className={css.togetherText}>
            <FormattedMessage id="Summary.together" />
          </p>
          <p className={css.togetherText}>{formatMoney(intl, payinTotal)}</p>
        </div>
      </div>

      <button
        className={classNames(css.bookButton, { [css.disabled]: isBookDisabled })}
        onClick={handleBook}
        disabled={isBookDisabled}
      >
        {inProgress ? <IconSpinner className={css.spinner} /> : <FormattedMessage id="Summary.bookButton" />}
      </button>

      {false && canBookInternally && (
        <button className={classNames(css.bookButton, css.internalBookButton)} onClick={handleBookInternally}>
          <FormattedMessage id="Summary.internalBooking" />
        </button>
      )}

      {isAddToUserAvailable && (
        <button className={css.addToUserButton} onClick={handleOpenUserModal} disabled={isBookDisabled}>
          {inProgress ? <IconSpinner className={css.spinner} /> : <FormattedMessage id="Summary.addToUser" />}
        </button>
      )}
      <AddToUserModal
        id={id}
        isOpen={isModalOpen}
        onCloseModal={() => setIsModalOpen(false)}
        cartListings={cartListings}
        onAddCartToUser={onAddCartToUser}
      />
    </div>
  );
};

export default Summary;
